import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, patch, post } from '../../lib/api.js';
import type { PublicUser } from '../../lib/auth.js';
import { errorMessage } from '../../lib/passkey.js';
import {
  Alert,
  fmtDate,
  fmtMonth,
  passkeyHomeClass,
  passkeyHomeLabel,
  phoneTakenMessage,
  Spinner,
} from '../../lib/ui.js';
import {
  digits10,
  emptyWorkerForm,
  WorkerForm,
  workerPayload,
  type WorkerFieldErrors,
  type WorkerFormValues,
  type Designation,
} from '../WorkerForm.js';
import { fmtPhone, type PasskeyHome } from '@siva/shared';
import { useConfirm } from '../../lib/Modal.js';
import { AgreementCard } from '../AgreementCard.js';
import { BankCard } from '../BankCard.js';
import { FeeCard } from '../FeeCard.js';
import { SlideOver } from '../../lib/SlideOver.js';
import { AdjustButton, AdjustDay, AdjustSelectionBar, type AdjustRequest } from '../AdjustDay.js';
import { Avatar } from '../../lib/components.js';
import { usePhotos } from '../photos.js';
import { PhotoPicker, photoSrc } from '../../lib/PhotoPicker.js';
import { PhotoViewer } from '../../lib/PhotoViewer.js';
import { t } from '../../i18n/index.js';

type Detail = {
  /** Whether this worker has the app, or has been excused from needing it. */
  app_bound_at?: string | null;
  app_exempt?: boolean;
  app_exempt_reason?: string | null;
  worker: PublicUser;
  photo_updated_at: string | null;
  passkey: {
    registered: boolean;
    since?: string;
    last_used_at?: string | null;
    device?: string | null;
    device_type?: string | null;
    /** What the authenticator said it was when the passkey was made. */
    home?: PasskeyHome;
    /** The raw id, shown when we cannot name it, so a new one can be identified not guessed. */
    aaguid?: string | null;
  };
  open_invite: { purpose: string; expires_at: string } | null;
};
type Month = {
  month: string;
  today: string;
  days_in_month: number;
  first_weekday: number;
  days: {
    date: string;
    status: 'present' | 'present_no_punchout' | 'absent' | 'weekly_off' | 'holiday';
    worked_minutes: number;
    target_minutes: number;
    auto_closed: boolean;
    worked_on_off: boolean;
    corrections_count: number;
    adjustment: Adjustment | null;
  }[];
  totals: {
    worked: number;
    target: number;
    shortfall: number;
    overtime: number;
    present: number;
    absent: number;
    off: number;
    holiday: number;
  };
  no_punchout_count: number;
  off_this_week: string | null;
};

/** What the day was corrected from and to, with the reason the admin gave. */
type Adjustment = { from: string; to: string; note: string; label: string };

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
const shiftMonth = (m: string, n: number) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mo - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function WorkerDetailPage() {
  // A photo changed here shows on every other admin list without a reload.
  const { refresh: refreshPhotos } = usePhotos();
  const confirm = useConfirm();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const wanted = params.get('date');
  const wantedDate = wanted && /^\d{4}-\d{2}-\d{2}$/.test(wanted) ? wanted : null;
  const [detail, setDetail] = useState<Detail | null>(null);
  const [month, setMonth] = useState<string | null>(wantedDate ? wantedDate.slice(0, 7) : null);
  const [grid, setGrid] = useState<Month | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [editing, setEditing] = useState(false);
  const [adjust, setAdjust] = useState<AdjustRequest | null>(null);
  /**
   * Choosing days on the calendar instead of out of a list.
   *
   * `picking` is a mode rather than a change to what a click means, on purpose: it is entered on
   * a labelled button and says what it is doing, rather than a click quietly meaning something new.
   */
  /** The photograph, open at full size. Null when it is not. */
  const [viewing, setViewing] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** The last cell clicked, so shift-click can take the run between the two. */
  const [anchor, setAnchor] = useState<string | null>(null);
  const [form, setForm] = useState<WorkerFormValues>(emptyWorkerForm);
  const [fieldErrors, setFieldErrors] = useState<WorkerFieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  /**
   * Mark somebody as unable to use the app, or take the mark off. It takes a reason because the
   * question it answers later is "why is this one person allowed to punch from a browser", and
   * an unexplained exemption is the kind that outlives the phone it was granted for.
   */
  const toggleExempt = async () => {
    if (!detail) return;
    const exempt = !detail.app_exempt;
    const reason = window.prompt(exempt ? t('wd_app_exempt_why') : t('wd_app_unexempt_why'));
    if (!reason || reason.trim().length < 3) return;
    setStatusBusy(true);
    setError(null);
    try {
      const r = await post<{ app_exempt: boolean; app_exempt_reason: string | null }>(
        `/admin/workers/${id}/app-exempt`,
        { exempt, reason: reason.trim() },
      );
      setDetail((d) =>
        d ? { ...d, app_exempt: r.app_exempt, app_exempt_reason: r.app_exempt_reason } : d,
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  };

  /** Deactivate (confirmed: revokes passkey, sessions and open links) or reactivate. */
  const setActive = async (active: boolean) => {
    if (!detail) return;
    if (
      !active &&
      !(await confirm({
        title: t('deactivate'),
        body: t('worker_deactivate_confirm', { name: detail.worker.name }),
        confirmLabel: t('deactivate'),
        danger: true,
      }))
    )
      return;
    setStatusBusy(true);
    setError(null);
    try {
      const r = await post<{ worker: PublicUser }>(
        `/admin/workers/${id}/${active ? 'reactivate' : 'deactivate'}`,
      );
      setDetail((d) => (d ? { ...d, worker: r.worker } : d));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  };

  const startEdit = (w: PublicUser) => {
    setForm({
      first_name: w.first_name ?? w.name.split(' ')[0] ?? '',
      last_name: w.last_name ?? w.name.split(' ').slice(1).join(' '),
      phone: digits10(w.phone),
      employment_type: w.employment_type ?? 'full_time',
      shift: w.shift ?? 'first',
      join_date: w.join_date ?? '',
      engagement_type: w.engagement_type ?? 'employee',
      designation_id: w.designation_id ?? null,
    });
    setFieldErrors({});
    setSaved(false);
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await patch<{ worker: PublicUser }>(
        `/admin/workers/${id}`,
        workerPayload(form, 'edit'),
      );
      setDetail((d) => (d ? { ...d, worker: r.worker } : d));
      setEditing(false);
      setSaved(true);
    } catch (err) {
      const phone = phoneTakenMessage(err);
      if (phone) setFieldErrors((fe) => ({ ...fe, phone }));
      else setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    // The admin-managed list from Settings. A failure here is not worth a message: the dropdown
    // simply has nothing in it, and the rest of the form still works.
    api<{ designations: Designation[] }>('/admin/designations')
      .then((r) => setDesignations(r.designations))
      .catch(() => {});
  }, []);

  const loadDetail = useCallback(async () => {
    try {
      setDetail(await api<Detail>(`/admin/workers/${id}`));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [id]);
  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const loadMonth = useCallback(async () => {
    try {
      const g = await api<Month>(`/admin/workers/${id}/month${month ? `?month=${month}` : ''}`);
      setGrid(g);
      if (!month) setMonth(g.month);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [id, month]);
  useEffect(() => {
    void loadMonth();
  }, [loadMonth]);

  if (error && !detail) return <Alert>{error}</Alert>;
  if (!detail || !grid) return <Spinner />;
  const w = detail.worker;
  const byDate = new Map(grid.days.map((d) => [d.date, d]));
  const cells: (string | null)[] = [];
  const offset = (grid.first_weekday + 6) % 7; // Monday-first grid
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= grid.days_in_month; d++)
    cells.push(`${grid.month}-${String(d).padStart(2, '0')}`);

  return (
    <div className="stack">
      <section className="card">
        <p>
          <Link className="link-block" to="/workers">
            ← {t('nav_workers')}
          </Link>
        </p>
        <div className="row between">
          {/* The name alone: the id and the type are the first two rows of the table under it,
              and the owner asked why they were printed twice. The photograph sits beside it
              because this is the page the office opens to check they have the right person. */}
          <span className="person-head">
            <Avatar
              name={w.name}
              size={56}
              photoUrl={photoSrc(`/admin/workers/${w.id}/photo`, detail.photo_updated_at)}
              onOpen={() =>
                setViewing(photoSrc(`/admin/workers/${w.id}/photo`, detail.photo_updated_at))
              }
            />
            <span>
              <h2>{w.name}</h2>
              {!editing && (
                <PhotoPicker
                  url={`/admin/workers/${w.id}/photo`}
                  hasPhoto={!!detail.photo_updated_at}
                  onDone={() => {
                    void loadDetail();
                    refreshPhotos();
                  }}
                />
              )}
            </span>
          </span>
          {!editing && (
            <span className="row">
              <button type="button" className="small secondary" onClick={() => startEdit(w)}>
                {t('worker_edit')}
              </button>
              {w.active ? (
                <button
                  type="button"
                  className="small danger"
                  disabled={statusBusy}
                  onClick={() => void setActive(false)}
                >
                  {t('worker_deactivate')}
                </button>
              ) : (
                <button
                  type="button"
                  className="small"
                  disabled={statusBusy}
                  onClick={() => void setActive(true)}
                >
                  {t('worker_reactivate')}
                </button>
              )}
            </span>
          )}
        </div>
        {error && <Alert>{error}</Alert>}
        {saved && !editing && <Alert kind="success">{t('worker_updated')}</Alert>}
        {viewing && <PhotoViewer src={viewing} name={w.name} onClose={() => setViewing(null)} />}
        <SlideOver open={editing} title={t('worker_edit')} onClose={() => setEditing(false)}>
          <WorkerForm
            code={w.employee_code}
            designations={designations}
            mode="edit"
            values={form}
            errors={fieldErrors}
            busy={saving}
            onChange={(next, clear) => {
              setForm(next);
              if (clear) setFieldErrors((fe) => ({ ...fe, [clear]: null }));
            }}
            onSubmit={save}
            onCancel={() => setEditing(false)}
          />
        </SlideOver>
      </section>

      {/* The approved layout of 15 October: the facts beside the month, then bank, salary and
          agreement side by side. The cards and everything in them are unchanged; only where they
          sit. One column below 1100 px. */}
      <div className="detail-grid">
        <section className="card detail-facts">
          <h3>{t('wd_details')}</h3>
          {/* Every fact in one table, the employee id first: the owner found the loose
            definition list unprofessional and the id missing. */}
          <table className="kv worker-facts">
            <tbody>
              <tr>
                <th scope="row">{t('profile_code')}</th>
                <td>{w.employee_code ?? '—'}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_designation')}</th>
                <td>{designations.find((d) => d.id === w.designation_id)?.name ?? '—'}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_type')}</th>
                <td>{w.employment_type ? t(w.employment_type) : '—'}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_phone')}</th>
                <td>{fmtPhone(w.phone)}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_join_date')}</th>
                <td>{fmtDate(w.join_date, false)}</td>
              </tr>
              <tr>
                <th scope="row">{t('wd_off_this_week')}</th>
                <td>{grid.off_this_week ? fmtDate(grid.off_this_week, false) : '—'}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_passkey')}</th>
                <td>
                  {detail.passkey.registered
                    ? t('passkey_registered_since', { date: fmtDate(detail.passkey.since, false) })
                    : t('passkey_not_registered')}
                  {detail.passkey.registered && detail.passkey.device && (
                    <>
                      <br />
                      <span className="muted device-label">
                        {t('passkey_device')}: {detail.passkey.device}
                      </span>
                    </>
                  )}
                  {detail.passkey.registered && detail.passkey.home && (
                    <>
                      <br />
                      <span className={passkeyHomeClass(detail.passkey.home)}>
                        {t('passkey_home')}: {passkeyHomeLabel(detail.passkey.home)}
                      </span>
                      {/* An id we cannot name is printed, so it can be identified and added to the
                        table rather than guessed at from the outside. */}
                      {detail.passkey.home === 'unknown' && detail.passkey.aaguid && (
                        <>
                          {' '}
                          <small className="muted">({detail.passkey.aaguid})</small>
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">{t('wd_no_punchout_month')}</th>
                <td>{grid.no_punchout_count}</td>
              </tr>
              <tr>
                <th scope="row">{t('worker_status')}</th>
                <td>{w.active ? t('worker_active') : t('worker_inactive')}</td>
              </tr>
              <tr>
                <th scope="row">{t('wd_app')}</th>
                <td>
                  {/*
                  One line, and only the part that does something. Whether they have punched from
                  the app is not shown: it is not actionable, and it was the same sentence on
                  every page. What matters here is the exemption, because an employee who cannot
                  use the app and is not marked cannot punch at all.
                */}
                  {detail.app_exempt ? (
                    <>
                      <span className="muted">{t('wd_app_exempt')}</span>
                      {detail.app_exempt_reason && (
                        <span className="muted"> — {detail.app_exempt_reason}</span>
                      )}{' '}
                      <button
                        type="button"
                        className="link small"
                        disabled={statusBusy}
                        onClick={() => void toggleExempt()}
                      >
                        {t('wd_app_unexempt_do')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="link small"
                      disabled={statusBusy}
                      onClick={() => void toggleExempt()}
                    >
                      {t('wd_app_exempt_do')}
                    </button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="card detail-calendar">
          <div className="row between">
            <h3>{fmtMonth(grid.month)}</h3>
            <span className="row">
              {/* One worker, several days: a stretch of leave, or a phone that was broken all
                week. Same endpoint as the day-at-a-time form, same mandatory note. */}
              <AdjustButton
                label={picking ? t('adj_done') : t('adj_select_days')}
                onClick={() => {
                  setPicking((on) => !on);
                  setPicked(new Set());
                  setAnchor(null);
                }}
              />
              <button
                type="button"
                className="small secondary"
                onClick={() => setMonth(shiftMonth(grid.month, -1))}
              >
                ‹
              </button>
              <button
                type="button"
                className="small secondary"
                onClick={() => setMonth(shiftMonth(grid.month, 1))}
              >
                ›
              </button>
            </span>
          </div>
          {/* The month in four numbers, each with the colour its days have below. */}
          <p className="dot-chips">
            <span>
              <i className="dot present" aria-hidden="true" />
              {t('hours_st_present')} <b>{grid.totals.present}</b>
            </span>
            <span>
              <i className="dot absent" aria-hidden="true" />
              {t('hours_st_absent')} <b>{grid.totals.absent}</b>
            </span>
            <span>
              <i className="dot off" aria-hidden="true" />
              {t('hours_st_off')} <b>{grid.totals.off}</b>
            </span>
            <span>
              <i className="dot no-out" aria-hidden="true" />
              {t('hours_st_no_punchout')} <b>{grid.no_punchout_count}</b>
            </span>
          </p>
          {picking && <p className="muted">{t('adj_selecting_days')}</p>}
          <div className="month-grid" role="grid">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="dow">
                {d}
              </div>
            ))}
            {cells.map((date, i) => {
              const s = date ? byDate.get(date) : undefined;
              const cls = date ? (s ? `st-${s.status}` : 'st-future') : 'empty';
              return (
                <button
                  type="button"
                  key={i}
                  className={[
                    'cell',
                    cls,
                    // The day the URL asked for stays marked: a flag's "See The Day" and the week
                    // grid both arrive here with ?date=, and the panel that used to answer them is
                    // gone. The highlight is what is left of that answer.
                    // `date !== null`: the blank cells before the 1st have no date, and with no
                    // ?date= in the URL either, null === null marked all of them selected.
                    date !== null && date === wantedDate && !picking ? 'selected' : '',
                    picking ? 'picking' : '',
                    date && picked.has(date) ? 'picked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={!date || !s}
                  aria-pressed={picking && date ? picked.has(date) : undefined}
                  onClick={(e) => {
                    if (!date) return;
                    if (!picking) return;
                    const days = cells.filter((c): c is string => !!c);
                    setPicked((prev) => {
                      const next = new Set(prev);
                      // Shift-click takes everything between the last click and this one, which is
                      // how a fortnight of leave gets selected without fourteen clicks.
                      if (e.shiftKey && anchor) {
                        const from = days.indexOf(anchor);
                        const to = days.indexOf(date);
                        if (from >= 0 && to >= 0) {
                          const [lo, hi] = from < to ? [from, to] : [to, from];
                          for (const d of days.slice(lo, hi + 1)) if (byDate.has(d)) next.add(d);
                          return next;
                        }
                      }
                      if (next.has(date)) next.delete(date);
                      else next.add(date);
                      return next;
                    });
                    setAnchor(date);
                  }}
                  title={
                    s
                      ? s.adjustment
                        ? `${s.status} · ${hm(s.worked_minutes)} — ${s.adjustment.label}`
                        : `${s.status} · ${hm(s.worked_minutes)}`
                      : ''
                  }
                >
                  {date ? Number(date.slice(8)) : ''}
                  {s && (s.status === 'present' || s.status === 'present_no_punchout') && (
                    <small>{hm(s.worked_minutes)}</small>
                  )}
                  {s?.auto_closed && <small className="tag">auto</small>}
                  {s?.worked_on_off && <small className="tag">off+work</small>}
                  {s?.adjustment && <small className="tag adjusted">{t('adj_marker')}</small>}
                </button>
              );
            })}
          </div>
          <p className="legend">
            <span className="st-present">present</span>{' '}
            <span className="st-present_no_punchout">no punch-out</span>{' '}
            <span className="st-absent">absent</span>{' '}
            <span className="st-weekly_off">weekly off</span>{' '}
            <span className="st-holiday">holiday</span>
          </p>
        </section>
      </div>

      <div className="detail-trio">
        <BankCard workerId={w.id} />
        <FeeCard workerId={w.id} />
        <AgreementCard workerId={w.id} />
      </div>

      {picking && (
        <AdjustSelectionBar
          count={picked.size}
          subtitle={`${detail?.worker.name ?? ''} · ${fmtMonth(grid.month)}`}
          onCancel={() => {
            setPicking(false);
            setPicked(new Set());
            setAnchor(null);
          }}
          onApply={async (status, note) => {
            await post('/admin/adjustments/bulk', {
              targets: [...picked].map((date) => ({ user_id: id, date })),
              status,
              note,
            });
            setPicked(new Set());
            setAnchor(null);
            setPicking(false);
            await loadMonth();
          }}
        />
      )}
      <AdjustDay
        request={adjust}
        onClose={() => setAdjust(null)}
        onSaved={() => {
          void loadMonth();
        }}
      />
      {/* No day panel. It showed the selected day's punches and map under the calendar and
          carried an Adjust Day button; today was pre-selected, so with no punches it opened on
          "No punches yet today". The owner asked for it to go (17 September 2026). A single day
          is adjusted from the calendar's Select Days or from the Attendance page's row; a day's
          punches are on the Attendance day view. */}
    </div>
  );
}

/**
 * Correct one day. The computed figures are never overwritten: this records what the day should
 * read as and why, and every view applies it on top. Removing the correction restores the
 * computed day exactly.
 */
